import React, { useEffect, useState } from "react";
import { SpinningLoader } from "risa-oasis-ui_v2";

/**
 * Props for the useTooltipData hook
 */
interface UseTooltipDataProps {
  /** Whether the tooltip is currently visible */
  showTooltip: boolean;
  /** Optional async function to fetch tooltip data. If provided, data will be fetched lazily when tooltip is shown */
  fetchFn?: () => Promise<string>;
  /** Static display text to show when fetchFn is not provided */
  displayText?: string;
}

/**
 * Return type for the useTooltipData hook
 */
interface UseTooltipDataResult {
  /** Whether data is currently being fetched */
  isLoading: boolean;
  /** The content to render in the tooltip (can be a loading spinner, fetched data, or static text) */
  tooltipContent: React.ReactNode;
}

/**
 * Custom hook for managing tooltip data fetching.
 *
 * This hook handles lazy loading of tooltip content - it only fetches data when the tooltip
 * becomes visible for the first time. Once fetched, the data is cached and won't be refetched.
 *
 * @param props - The hook configuration options
 * @param props.showTooltip - Whether the tooltip is currently visible
 * @param props.fetchFn - Optional async function to fetch tooltip data
 * @param props.displayText - Static text to display if no fetchFn is provided
 *
 * @returns An object containing:
 *   - isLoading: Whether data is currently being fetched
 *   - tooltipContent: The React node to render in the tooltip
 *
 * @example
 * // With async data fetching
 * const { tooltipContent } = useTooltipData({
 *   showTooltip: isHovered,
 *   fetchFn: () => fetchUserDetails(userId),
 * });
 *
 * @example
 * // With static text
 * const { tooltipContent } = useTooltipData({
 *   showTooltip: isHovered,
 *   displayText: "Click to edit",
 * });
 */
export const useTooltipData = ({
  showTooltip,
  fetchFn,
  displayText,
}: UseTooltipDataProps): UseTooltipDataResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [fetchedData, setFetchedData] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch data when tooltip is shown and fetchFn is provided
  useEffect(() => {
    if (showTooltip && fetchFn && !hasFetched) {
      setIsLoading(true);
      fetchFn()
        .then((data) => {
          setFetchedData(data);
          setHasFetched(true);
        })
        .catch((error) => {
          console.error("Error fetching data for tooltip:", error);
          setFetchedData("");
          setHasFetched(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [showTooltip, fetchFn, hasFetched]);

  // Determine the content to display in tooltip
  const getTooltipContent = (): React.ReactNode => {
    if (fetchFn) {
      if (isLoading) {
        return React.createElement(
          "div",
          { className: "flex items-center gap-2" },
          React.createElement(SpinningLoader),
        );
      }
      return fetchedData ?? "";
    }
    return displayText ?? "";
  };

  return {
    isLoading,
    tooltipContent: getTooltipContent(),
  };
};
